/**
 * Shared graded-claim card — the durable piece the extraction review AND the
 * Kompass evidence ledger both render (reuse, not copy). Grade + status are legible
 * as registered `is-*` classes; confirm / edit / discard are `data-act` verbs
 * matching the api verbs (AX-3). The host element owns the handlers.
 */

import { classMap, html, type TemplateResult } from "@platform/web-kit/render"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { EvidenceClaimView, EvidenceGrade } from "../domain"

export const GRADE_KEY: Record<EvidenceGrade, CareerKey> = {
  evidenced: "review.grade.evidenced",
  "self-positioning": "review.grade.selfPositioning",
  gap: "review.grade.gap",
}

export interface ClaimCardOpts {
  editing: boolean
  t: CareerI18n["t"]
}

/** Render one graded claim as an `<li urn=…>` card. */
export function renderClaimCard(claim: EvidenceClaimView, { editing, t }: ClaimCardOpts): TemplateResult {
  return html`
    <li
      urn=${claim.urn}
      class=${classMap({ "claim-card": true, [`is-${claim.grade}`]: true, [`is-${claim.status}`]: true })}
    >
      <span class="claim-grade chip" lang="de">${t(GRADE_KEY[claim.grade])}</span>
      ${editing
        ? html`<input class="claim-edit-input" type="text" .value=${claim.statement} aria-label=${t("review.editLabel")} />`
        : html`<p class="claim-statement" lang="de">${claim.statement}</p>`}
      <span class="claim-sources chip" lang="de">${t("review.sources", { n: claim.sources.length })}</span>
      ${claim.tags.map((tag) => html`<span class="claim-tag chip" lang="de">${tag.label}</span>`)}
      <div class="claim-actions">
        ${claim.status === "proposed"
          ? html`<button data-act="confirm-claim" data-claim-id=${claim.id} lang="de">${t("review.confirm")}</button>`
          : html`<span class="claim-confirmed chip is-confirmed" lang="de">${t("review.confirmed")}</span>`}
        ${editing
          ? html`<button data-act="edit-claim" data-claim-id=${claim.id} lang="de">${t("review.save")}</button>`
          : html`<button data-act="edit-toggle" data-claim-id=${claim.id} lang="de">${t("review.edit")}</button>`}
        <button data-act="discard-claim" data-claim-id=${claim.id} lang="de">${t("review.discard")}</button>
      </div>
    </li>
  `
}
